import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Project } from "@/types/crm";
import { format } from "date-fns";

interface ProjectWithOrganization extends Omit<Project, 'organization'> {
  organization?: {
    id: string;
    name: string;
  } | null;
}

interface ProjectSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProject: (project: Project) => void;
}

export const ProjectSelectorModal = ({ isOpen, onClose, onSelectProject }: ProjectSelectorModalProps) => {
  const [projects, setProjects] = useState<ProjectWithOrganization[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchCompletedProjects = async () => {
    setLoading(true);
    try {
      // Get completed projects that are not already linked to a site
      const { data: completedProjects, error: projectsError } = await supabase
        .from('projects')
        .select(`
          *,
          organization:organizations(
            id,
            name
          )
        `)
        // T1.1 — 'afgerond' is geen stadium meer maar een status. Dit stond op
        // .eq('stage','afgerond') en zou na migratie 030 altijd nul rijen geven.
        .eq('status', 'afgerond')
        .is('deleted_at', null) as { data: ProjectWithOrganization[] | null, error: any };

      if (projectsError) throw projectsError;

      // Get projects that are already linked to sites
      const { data: linkedProjects, error: sitesError } = await supabase
        .from('sites')
        .select('project_id')
        .not('project_id', 'is', null);

      if (sitesError) throw sitesError;

      const linkedProjectIds = new Set(linkedProjects?.map(site => site.project_id));
      
      // Filter out already linked projects
      const availableProjects = completedProjects?.filter(
        project => !linkedProjectIds.has(project.id)
      ) || [];

      setProjects(availableProjects);
    } catch (error) {
      console.error('Error fetching completed projects:', error);
      toast({
        title: 'Fout bij laden projecten',
        description: 'Er ging iets mis bij het ophalen van de afgeronde projecten.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCompletedProjects();
    }
  }, [isOpen]);

  const formatAmount = (amount: number | null) => {
    if (!amount) return '';
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Selecteer afgerond project</DialogTitle>
          <DialogDescription>
            Kies een afgerond project om een locatie van te maken
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="text-muted-foreground">Projecten laden...</div>
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Geen afgeronde projecten beschikbaar die nog niet als locatie zijn aangemaakt.
            </div>
          ) : (
            <div className="grid gap-3">
              {projects.map((project) => (
                <Card key={project.id} className="cursor-pointer hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{project.name}</CardTitle>
                        <CardDescription>
                          {project.organization?.name && `${project.organization.name} • `}
                          {project.type && `${project.type} • `}
                          {project.created_at && format(new Date(project.created_at), 'dd/MM/yyyy')}
                        </CardDescription>
                      </div>
                      <div className="text-right">
                        <Badge variant="outline" className="mb-2">
                          {project.stage}
                        </Badge>
                        {project.amount && (
                          <div className="text-sm font-medium text-muted-foreground">
                            {formatAmount(project.amount)}
                          </div>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                     <Button 
                       className="w-full" 
                       onClick={() => {
                         onSelectProject(project as Project);
                         onClose();
                       }}
                     >
                      Selecteer project
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};